// components/EpicLabResults.js
//
// Displays synced lab values (from Epic or any other source), with trend
// charts for any lab that has 2+ readings over time.
//
// Two modes:
//   <EpicLabResults />                  — self-view (patient's own /profile)
//   <EpicLabResults patientId={123} />  — provider view, adds a Sync Now button
//
// FEATURE FLAG: set REACT_APP_ENABLE_EPIC=false to hide this component
// entirely (renders null) — see ConnectEpicButton.js for the matching flag.
//
// api.js additions needed:
//   getEpicLabs, getEpicLabsForPatient, syncEpicRecordsForPatient
// (see previous versions of this file for exact snippets)

import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';

const EPIC_ENABLED = process.env.REACT_APP_ENABLE_EPIC !== 'false';

const SOURCE_LABELS = {
  epic_fhir: { label: 'From Epic', color: '#1A5EA8' },
  lab_upload_ocr: { label: 'Uploaded', color: '#0ab5a0' },
  manual_entry: { label: 'Manual', color: '#94a3b8' },
  app_helper_chat: { label: 'Care Copilot', color: '#f59e0b' },
};

const CHART_COLORS = ['#1A5EA8', '#0ab5a0', '#e8a020', '#e05c3a', '#0B2545', '#059669'];

// Sensible default units per field, prefilled but editable in the manual
// entry form — matches typical units for values Epic sync would return.
const DEFAULT_UNITS = {
  egfr: 'mL/min/1.73m²',
  ntProBnp: 'pg/mL',
  troponinI: 'ng/mL',
  troponinT: 'ng/mL',
  freeLightChainKappa: 'mg/L',
  freeLightChainLambda: 'mg/L',
  freeLightChainRatio: '',
  potassium: 'mEq/L',
  calcium: 'mg/dL',
  phosphorus: 'mg/dL',
  pth: 'pg/mL',
  hemoglobin: 'g/dL',
};

function TrendChart({ points, color }) {
  const width = 320;
  const height = 90;
  const padding = 24;

  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = (width - padding * 2) / (points.length - 1);

  const coords = points.map((p, i) => {
    const x = padding + i * stepX;
    const y = padding + (height - padding * 2) * (1 - (p.value - min) / range);
    return { x, y, value: p.value, date: p.date };
  });

  const polylinePoints = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const trendUp = values[values.length - 1] > values[0];
  const trendFlat = values[values.length - 1] === values[0];

  return (
    <div style={{ display: 'inline-block' }}>
      <svg width={width} height={height} style={{ overflow: 'visible' }}>
        <polyline points={polylinePoints} fill="none" stroke={color} strokeWidth="2" />
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r="3.5" fill={color} />
        ))}
        <text x={coords[0].x} y={coords[0].y - 10} fontSize="10" fill="#64748b" textAnchor="middle">
          {coords[0].value}
        </text>
        <text
          x={coords[coords.length - 1].x}
          y={coords[coords.length - 1].y - 10}
          fontSize="10"
          fill="#64748b"
          textAnchor="middle"
        >
          {coords[coords.length - 1].value}
        </text>
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
        <span>{coords[0].date}</span>
        <span style={{ fontWeight: 700, color: trendFlat ? '#94a3b8' : trendUp ? '#ef4444' : '#10b981' }}>
          {trendFlat ? '→ stable' : trendUp ? '↑ rising' : '↓ falling'}
        </span>
        <span>{coords[coords.length - 1].date}</span>
      </div>
    </div>
  );
}

function AddLabDialog({ allowedFields, onCancel, onSaved, patientId }) {
  const [fieldKey, setFieldKey] = useState('');
  const [valueNumeric, setValueNumeric] = useState('');
  const [unit, setUnit] = useState('');
  const [resultDate, setResultDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFieldChange = (e) => {
    const key = e.target.value;
    setFieldKey(key);
    setUnit(DEFAULT_UNITS[key] || '');
  };

  const handleSubmit = async () => {
    if (!fieldKey || valueNumeric === '') {
      setError('Please select a lab type and enter a value.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await apiService.addManualLabValue(patientId, {
        fieldKey,
        valueNumeric: Number(valueNumeric),
        unit,
        resultDate,
      });
      onSaved();
    } catch (e) {
      setError('Failed to save — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const cardStyle = {
    background: 'white', borderRadius: 14, padding: 24, width: 360,
    fontFamily: "'DM Sans', system-ui, sans-serif", boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  };
  const fieldStyle = {
    width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, marginTop: 4, marginBottom: 14, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: '#334155' };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Add Lab Result</h3>

        <label style={labelStyle}>Lab Type</label>
        <select style={fieldStyle} value={fieldKey} onChange={handleFieldChange}>
          <option value="">Select a lab type…</option>
          {Object.entries(allowedFields).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <label style={labelStyle}>Value</label>
        <input
          style={fieldStyle}
          type="number"
          step="any"
          value={valueNumeric}
          onChange={(e) => setValueNumeric(e.target.value)}
        />

        <label style={labelStyle}>Unit</label>
        <input style={fieldStyle} type="text" value={unit} onChange={(e) => setUnit(e.target.value)} />

        <label style={labelStyle}>Result Date</label>
        <input
          style={fieldStyle}
          type="date"
          value={resultDate}
          onChange={(e) => setResultDate(e.target.value)}
        />

        {error && <div style={{ color: '#ef4444', fontSize: 12.5, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            onClick={onCancel}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: submitting ? '#94a3b8' : '#1A5EA8', color: 'white',
              cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600,
            }}
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const EpicLabResults = ({ patientId }) => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [allowedFields, setAllowedFields] = useState({});

  const loadLabs = useCallback(async () => {
    if (!EPIC_ENABLED) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = patientId
        ? await apiService.getEpicLabsForPatient(patientId)
        : await apiService.getEpicLabs();
      setLabs(Array.isArray(data) ? data : []);
    } catch (e) {
      setLabs([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadLabs();
  }, [loadLabs]);

  useEffect(() => {
    if (patientId) {
      apiService.getAllowedManualLabFields(patientId).then(setAllowedFields).catch(() => {});
    }
  }, [patientId]);

  const handleDelete = async (labValueId) => {
    if (!window.confirm('Delete this manually-entered lab result?')) return;
    try {
      await apiService.deleteManualLabValue(patientId, labValueId);
      await loadLabs();
    } catch (e) {
      // silent — the row just stays visible if the delete failed
    }
  };

  const handleSyncNow = async () => {
    setSyncResult(null);
    setSyncing(true);
    try {
      const result = await apiService.syncEpicRecordsForPatient(patientId);
      setSyncResult({
        ok: true,
        text: `Synced: ${result?.labsImported ?? 0} labs, ${result?.conditionsImported ?? 0} conditions`,
      });
      await loadLabs();
    } catch (e) {
      setSyncResult({ ok: false, text: 'Sync failed — patient may not have an active Epic connection' });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 6000);
    }
  };

  if (!EPIC_ENABLED) return null;
  if (loading) return null;

  const grouped = {};
  labs.forEach((lab) => {
    const key = lab.fieldKey;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(lab);
  });

  const trendGroups = Object.entries(grouped)
    .map(([fieldKey, entries]) => {
      const withDates = entries.filter((e) => e.resultDate && e.valueNumeric != null);
      const points = withDates
        .map((e) => ({ date: e.resultDate, value: e.valueNumeric }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      return { fieldKey, testName: entries[0].testName || fieldKey, unit: entries[0].unit, points };
    })
    .filter((g) => g.points.length >= 2);

  const chartedFieldKeys = new Set(trendGroups.map((g) => g.fieldKey));
  const singleReadingLabs = labs.filter((lab) => !chartedFieldKeys.has(lab.fieldKey));

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🧪 Lab Results</h3>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            {patientId ? "Synced from this patient's connected health records" : 'Synced from connected health records'}
          </p>
        </div>

        {patientId && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowAddDialog(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 10,
                  background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  color: '#059669',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                }}
              >
                ➕ Add Lab Result
              </button>
              <button
                onClick={handleSyncNow}
                disabled={syncing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 10,
                  background: syncing ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.1)',
                  border: '1px solid rgba(102,126,234,0.3)',
                  color: '#1A5EA8',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                }}
              >
                {syncing ? '🔄 Syncing…' : '🔄 Sync Now'}
              </button>
            </div>
            {syncResult && (
              <div style={{ fontSize: 11.5, color: syncResult.ok ? '#10b981' : '#ef4444' }}>
                {syncResult.text}
              </div>
            )}
          </div>
        )}
      </div>

      {labs.length === 0 ? (
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 12 }}>
          {error
            ? 'Unable to load lab results.'
            : patientId
            ? 'No synced lab results for this patient yet.'
            : null}
        </p>
      ) : (
        <>
          {trendGroups.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 16, marginBottom: 20 }}>
              {trendGroups.map((group, i) => (
                <div
                  key={group.fieldKey}
                  style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white' }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 8 }}>
                    {group.testName}
                    {group.unit && <span style={{ fontWeight: 400, color: '#64748b' }}> ({group.unit})</span>}
                  </div>
                  <TrendChart points={group.points} color={CHART_COLORS[i % CHART_COLORS.length]} />
                </div>
              ))}
            </div>
          )}

          {singleReadingLabs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {singleReadingLabs.map((lab) => {
                const source = SOURCE_LABELS[lab.source] || { label: lab.source, color: '#94a3b8' };
                return (
                  <div
                    key={lab.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                      background: 'white',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{lab.testName || lab.fieldKey}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {lab.resultDate || 'Date unknown'}
                        {lab.loincCode && ` · LOINC ${lab.loincCode}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                          {lab.valueNumeric != null ? lab.valueNumeric : lab.valueText || '—'}
                        </span>
                        {lab.unit && <span style={{ fontSize: 12, color: '#64748b', marginLeft: 4 }}>{lab.unit}</span>}
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 999,
                          background: `${source.color}18`,
                          color: source.color,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {source.label}
                      </span>
                      {patientId && lab.source === 'manual_entry' && (
                        <button
                          onClick={() => handleDelete(lab.id)}
                          title="Delete this manual entry"
                          style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            color: '#94a3b8', fontSize: 14, padding: 0,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showAddDialog && (
        <AddLabDialog
          allowedFields={allowedFields}
          patientId={patientId}
          onCancel={() => setShowAddDialog(false)}
          onSaved={async () => {
            setShowAddDialog(false);
            await loadLabs();
          }}
        />
      )}
    </div>
  );
};

export default EpicLabResults;